import * as React from 'react'
import { useLayoutEffect, useState } from 'react'

export interface Dimensions {
  width: number
  height: number
}

// useResizeObserver used to have a bug in which the initial value of `size` was
// set to this empty size value, which meant that initial renders would consume
// this incorrect dummy value. We've since modified the behavior such that the
// initial value is null, but to support existing product behaviors we expose
// this const.
export const EMPTY_SIZE = { width: 0, height: 0 } as const

export function useResizeObserver(
  ref?: React.RefObject<HTMLElement | undefined>,
  onMeasure?: (size: Dimensions) => void,
): Dimensions | null {
  const [size, setSize] = useState<Dimensions | null>(null)
  // useLayoutEffect instead of useEffect because we need DOM mutations to be committed
  // before we subscribe to the elements size
  useLayoutEffect(() => {
    if (!ref || !ref.current) {
      return
    }

    // ResizeObservers allow you to be notified when a DOM node changes size.
    // They have pretty good browser support now (82% as of Sept 2020). We are currently
    // polyfilling this, but if you are reading this comment after Sept 2021 you should check
    // to see if we can remove the polyfill.  https://caniuse.com/resizeobserver
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (ref.current === entry.target) {
          const newWidth = entry.contentRect.width
          const newHeight = entry.contentRect.height
          const isSameSize = size && size.width === newWidth && size.height === newHeight
          if (isSameSize) {
            continue
          }

          const newSize = { width: entry.contentRect.width, height: entry.contentRect.height }
          setSize(newSize)
          onMeasure?.(newSize)
        }
      }
    })
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [ref, onMeasure, size])

  return size
}

/**
 * useResizeObserver requires a ref, but in some cases your ref may change the value
 * it's pointing to after multiple renders. This hook is a variant of useResizeObserver
 * that takes an element directly instead of a ref, allowing you to update the observer
 * subscription if the input element changes.
 */
export function useResizeObserverElement(
  element: HTMLElement | null,
  onMeasure?: (size: Dimensions) => void,
): Dimensions | null {
  const [size, setSize] = useState<Dimensions | null>(null)
  // useLayoutEffect instead of useEffect because we need DOM mutations to be committed
  // before we subscribe to the elements size
  useLayoutEffect(() => {
    if (!element) {
      return
    }

    // ResizeObservers allow you to be notified when a DOM node changes size.
    // They have pretty good browser support now (82% as of Sept 2020). We are currently
    // polyfilling this, but if you are reading this comment after Sept 2021 you should check
    // to see if we can remove the polyfill.  https://caniuse.com/resizeobserver
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (element === entry.target) {
          const newWidth = entry.contentRect.width
          const newHeight = entry.contentRect.height
          const isSameSize = size && size.width === newWidth && size.height === newHeight
          if (isSameSize) {
            continue
          }

          const newSize = { width: entry.contentRect.width, height: entry.contentRect.height }
          setSize(newSize)
          onMeasure?.(newSize)
        }
      }
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [element, onMeasure, size])

  return size
}
